/**
 * VectorsPassiveSkillEffect — Poison tree plague path passive (Chain sub-path
 * terminal): bonus DoT on Contagion hops.
 *
 * Each Contagion hop (including Chain-forced spreads and PlagueVector jumps)
 * has a 15% chance to apply a bonus independent 2-second poison DoT on top
 * of the transferred dose. The bonus DoT is a separate tick instance —
 * sourceSkillId 'vectors_bonus', intensity 3 (flat, not scaled by
 * Toxicologist), independent stacking.
 *
 * Mechanically, the passive provides two resolver keys:
 * - `jumpBonusDotChance`: 0.15 (max aggregation, tree scope)
 * - `jumpBonusDotDurationMs`: 2000 (max aggregation, tree scope)
 *
 * The actual bonus logic lives in the hop sites (ContagionSkillEffect,
 * PlagueVectorSkillEffect, ChainSkillEffect), which query the resolver
 * for these keys after each hop and roll independently for the bonus.
 *
 * This is one of two terminal passives required for Entropy (Apotheosis).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see poison-spec.md § Vectors (chain_passive)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface VectorsPassiveDeps {
  readonly resolver: {
    getTreePassiveEffect(
      treeName: string,
      key: string,
      aggregation: 'sum' | 'product' | 'max' | 'min',
    ): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface VectorsPassiveConfig {
  /** Fallback bonus chance if resolver returns 0 (should not happen when unlocked). */
  readonly defaultBonusChance: number;
  /** Fallback bonus duration in ms. */
  readonly defaultBonusDurationMs: number;
}

const DEFAULT_CONFIG: VectorsPassiveConfig = {
  defaultBonusChance: 0.15,
  defaultBonusDurationMs: 2000,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class VectorsPassiveSkillEffect implements SkillEffect {
  readonly skillId = 'chain_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: VectorsPassiveDeps;
  private readonly config: VectorsPassiveConfig;

  constructor(deps: VectorsPassiveDeps, config: Partial<VectorsPassiveConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  /** Bonus DoT chance per hop, from resolver (or fallback config). */
  get bonusDotChance(): number {
    const resolved = this.deps.resolver.getTreePassiveEffect(
      'poison', 'jumpBonusDotChance', 'max',
    );
    return resolved > 0 ? resolved : this.config.defaultBonusChance;
  }

  /** Bonus DoT duration in ms, from resolver (or fallback config). */
  get bonusDotDurationMs(): number {
    const resolved = this.deps.resolver.getTreePassiveEffect(
      'poison', 'jumpBonusDotDurationMs', 'max',
    );
    return resolved > 0 ? resolved : this.config.defaultBonusDurationMs;
  }

  // --- SkillEffect interface ---

  update(_delta: number): void {
    // Passive — no per-frame logic
  }

  destroy(): void {
    // Passive — nothing to clean up
  }
}
